import { IcoRemove } from '@/utils/icons'
import { FC, useEffect, useRef, useState } from 'react'
import { Modal } from 'antd'
import amclasses from '@pages/Admin/CRM/styles/ammodal.module.css'
import classes from './index.module.css'
import cn from 'classnames'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import CreateOrBindingStudentContent, {
  CreateOrBindingStudentContentHandle,
} from './CreateOrBindingStudentContent'
import Button from '@UI/New/Button'
import { useAddAlert } from '@/hooks/useAddAlert'
import SetClassModalBody from './SetClassModalBody'
import SetCourseModalBody from './SetCourseModalBody'
import type { LeadProfileLead } from '../../../../LeadProfileModal.types'

interface MassAction {
  student: null | 'CREATE' | 'BIND'
  purchaseType: 'COURSE' | 'CLASS_DIRECTION'
}

export type PurchaseHandleSaveFnParams = {
  users?: Array<{ id: number }>
  paymentFrom?: 'crm' | 'balance' | undefined
  othersParams?: Record<string, string>
}

export interface PurchaseHandle {
  handleSave: (
    props?: PurchaseHandleSaveFnParams,
  ) => Promise<'success' | 'error'>
}

interface StudentRefType extends CreateOrBindingStudentContentHandle {}

interface SetCourseClassDirectionModalProps {
  lead: LeadProfileLead
  currentLeadFunnelId?: number
  userId?: number | null
  excludedClassIds?: number[]
  excludedDirectionIds?: number[]
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

const SetCourseClassDirectionModal: FC<SetCourseClassDirectionModalProps> = ({
  lead,
  currentLeadFunnelId,
  userId: defUserId,
  excludedClassIds = [],
  excludedDirectionIds = [],
  open,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const [massAction, setMassAction] = useState<MassAction>({
    student: defUserId ? null : 'CREATE',
    purchaseType: 'COURSE',
  })

  const handlerUpdateMassAction = <K extends keyof MassAction>(
    type: K,
    value: MassAction[K],
  ) => {
    setMassAction((prev) => ({
      ...prev,
      [type]: value,
    }))
  }

  useEffect(() => {
    handlerUpdateMassAction('student', defUserId ? null : 'CREATE')
  }, [defUserId])

  // ------ SAVE ---------

  const [submitting, setSubmitting] = useState(false)

  const studentRef = useRef<StudentRefType>(null)
  const purchaseRef = useRef<PurchaseHandle>(null)

  const handleSave = async () => {
    setSubmitting(true)
    let userId = defUserId

    try {
      if (defUserId === null) {
        const result = await studentRef.current?.handleSave?.()
        if (result === 'error') return
        userId = result
      }

      if (!userId) {
        addAlert({
          title: 'Ошибка',
          text: 'Ошибка в личном кабинете студента',
          type: 'error',
        })
        setSubmitting(false)
        return
      }
      if (!currentLeadFunnelId) {
        addAlert({
          title: 'Ошибка',
          text: 'Не удалось определить воронку для добавления покупки',
          type: 'error',
        })
        setSubmitting(false)
        return
      }

      const result = await purchaseRef.current?.handleSave?.({
        users: [{ id: userId }],
        paymentFrom: 'crm',
        othersParams: {
          user_id: String(userId),
          lead_id: String(lead.id),
          lead_funnel_id: String(currentLeadFunnelId),
        },
      } as any)
      if (result === 'success') {
        onSuccess?.()
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <Modal
      open={open}
      onOk={onSuccess}
      onCancel={onClose}
      footer={null}
      title={null}
      centered
      maskClosable
      className={cn(
        amclasses.modal,
        classes.rubberLayoutMinimized,
        classes.disableAdaptRem,
      )}
    >
      <div className={classes.modalHeader}>
        <div className={classes.modalTitleContainer}>
          <span className={classes.modalHeaderTitle}>Добавить покупку</span>
        </div>
        <div className={classes.closeIcon} onClick={onClose}>
          <IcoRemove />
        </div>
      </div>
      <div className={classes.contentWrapper}>
        {massAction.student !== null && (
          <div className={classes.modalBlock}>
            <p className={classes.textDescription}>
              Этот лид пока не привязан ни к одному личному кабинету. Необходимо
              создать или привязать к существующему.
            </p>
            <div className={classes.selectTypeWrapper}>
              <button
                className={
                  massAction.student === 'CREATE'
                    ? classes.tabButtonActive
                    : classes.tabButton
                }
                onClick={() => handlerUpdateMassAction('student', 'CREATE')}
              >
                Создать ЛК
              </button>
              <TooltipWrapper title="Привязка к существующему личному кабинету студента">
                <button
                  className={
                    massAction.student === 'BIND'
                      ? classes.tabButtonActive
                      : classes.tabButton
                  }
                  onClick={() => handlerUpdateMassAction('student', 'BIND')}
                >
                  Привязать сущ. ЛК
                </button>
              </TooltipWrapper>
            </div>
            <CreateOrBindingStudentContent
              ref={studentRef}
              type={massAction.student}
              lead={lead}
            />
          </div>
        )}

        {/* ---- покупки ---- */}

        <div className={classes.modalBlock}>
          <div className={classes.selectTypeWrapper}>
            <button
              className={
                massAction.purchaseType === 'COURSE'
                  ? classes.tabButtonActive
                  : classes.tabButton
              }
              onClick={() => handlerUpdateMassAction('purchaseType', 'COURSE')}
            >
              Курс
            </button>
            <button
              className={
                massAction.purchaseType === 'CLASS_DIRECTION'
                  ? classes.tabButtonActive
                  : classes.tabButton
              }
              onClick={() =>
                handlerUpdateMassAction('purchaseType', 'CLASS_DIRECTION')
              }
            >
              Класс/Траектория
            </button>
          </div>
          {massAction.purchaseType === 'COURSE' ? (
            <SetCourseModalBody
              ref={purchaseRef}
              excludedDirectionIds={excludedDirectionIds}
            />
          ) : (
            <SetClassModalBody
              ref={purchaseRef}
              excludedClassIds={excludedClassIds}
            />
          )}
        </div>

        <div className={classes.footerActions}>
          <Button
            color="secondary"
            className={classes.submitButton}
            onClick={handleSave}
            disabled={submitting}
            isLoading={submitting}
          >
            Сохранить
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default SetCourseClassDirectionModal
